import React, { useEffect, useMemo, useState } from "react";
import Modal from "../../../components/Modal";
import Button from "../../../components/Button";
import Toggle from "../../../components/Toggle";
import Avatar from "../../../components/Avatar";
import api from "../../../services/api";
import { toast } from "react-toastify";
import { renderError } from "../../../helpers/errors";
import { hasPermission } from "../../../services/auth";

/**
 * Gestão dos usuários com acesso a um painel (pipeline).
 *  - Listar/visualizar é liberado para quem enxerga o painel.
 *  - Editar exige a permissão "manage_pipelines".
 *  - Remover um membro que ainda tem cards abertos no painel é permitido, mas a
 *    interface avisa antes de salvar — os cards permanecem, apenas deixam de ser
 *    vistos por quem perdeu o acesso.
 */
export const PipelineMembersModal = ({ pipeline, close }) => {
    const canManage = hasPermission('manage_pipelines');

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [admins, setAdmins] = useState([]);
    const [members, setMembers] = useState([]); // [{id, name, email, role_name, open_cards_count}]
    const [selectedIds, setSelectedIds] = useState([]);

    useEffect(() => {
        if (!pipeline?.id) return;
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pipeline?.id]);

    const load = () => {
        setLoading(true);
        Promise.all([
            api.get(`/pipeline/${pipeline.id}/admins`),
            canManage ? api.get(`/list`) : Promise.resolve(null),
        ]).then(([membersRes, adminsRes]) => {
            const memberList = membersRes.data?.resources ?? [];
            setMembers(memberList);
            setSelectedIds(memberList.map(member => member.id));

            if (adminsRes) {
                const raw = adminsRes.data?.resources ?? adminsRes.data?.data ?? [];
                setAdmins(Array.isArray(raw) ? raw : []);
            }
        }).catch(error => {
            renderError(error);
        }).then(() => setLoading(false));
    };

    // Mapa id -> cards em aberto do membro (para o aviso de remoção).
    const openCardsById = useMemo(() => {
        const map = {};
        members.forEach(member => { map[member.id] = member.open_cards_count ?? 0; });
        return map;
    }, [members]);

    // Ao gerenciar, mostramos todos os admins; os que não puderam ser carregados
    // (ex.: sem permissão) caem para a própria lista de membros.
    const rows = useMemo(() => {
        const base = canManage && admins.length > 0 ? admins : members;
        return [...base].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    }, [canManage, admins, members]);

    const toggle = id => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    // Membros que estão sendo removidos e ainda têm cards abertos no painel.
    const removedWithOpenCards = useMemo(() => members.filter(member =>
        !selectedIds.includes(member.id) && (member.open_cards_count ?? 0) > 0
    ), [members, selectedIds]);

    const save = () => {
        setSaving(true);
        api.put(`/pipeline/${pipeline.id}/admins`, { admins: selectedIds }).then(() => {
            toast.success('Acessos do painel atualizados');
            close(true);
        }).catch(error => {
            renderError(error);
        }).then(() => setSaving(false));
    };

    return (
        <Modal
            className={`pipeline-members-modal`}
            show={true}
            title={`Usuários com acesso · ${pipeline?.name ?? ''}`}
            close={() => { if (!saving) close(false); }}
            footer={canManage &&
                <>
                    <Button
                        type={`primary`}
                        size={`small`}
                        svg="save"
                        text={`Salvar acessos`}
                        action={save}
                        loading={saving}
                        disabled={loading}
                    />
                    {!saving &&
                        <Button type={`secondary`} size={`small`} text={`Cancelar`} action={() => close(false)} />
                    }
                </>
            }
        >
            <div className="section pipeline-members">
                {loading &&
                    <p className="pipeline-members__loading">Carregando…</p>
                }

                {!loading && !canManage &&
                    <p className="pipeline-members__hint">
                        Você não tem permissão para gerenciar os acessos deste painel. Abaixo, quem tem acesso hoje:
                    </p>
                }

                {!loading &&
                    <ul className="pipeline-members__list">
                        {rows.map(row => {
                            const checked = selectedIds.includes(row.id);
                            const openCards = openCardsById[row.id] ?? 0;
                            return (
                                <li key={row.id} className="pipeline-members__row">
                                    <div className="pipeline-members__info">
                                        <Avatar name={row.name} photo={row.photo_url} size={32} />
                                        <div className="pipeline-members__text">
                                            <span className="pipeline-members__name">{row.name}</span>
                                            <span className="pipeline-members__email">
                                                {row.email}{row.role_name ? ` · ${row.role_name}` : ''}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="pipeline-members__meta">
                                        {openCards > 0 &&
                                            <span className="pipeline-members__badge" title="Cards em aberto atribuídos a este usuário neste painel">
                                                {openCards} card{openCards === 1 ? '' : 's'} em aberto
                                            </span>
                                        }
                                        {canManage
                                            ? <Toggle checked={checked} onChange={() => toggle(row.id)} label={`Acesso de ${row.name}`} />
                                            : null
                                        }
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                }

                {!loading && canManage && removedWithOpenCards.length > 0 &&
                    <div className="pipeline-members__warning">
                        <strong>Atenção:</strong> ao salvar, {removedWithOpenCards.length === 1 ? 'este usuário deixará' : 'estes usuários deixarão'} de
                        ver o painel, incluindo cards abertos que ainda são responsabilidade
                        {removedWithOpenCards.length === 1 ? ' dele' : ' deles'}:
                        <ul>
                            {removedWithOpenCards.map(member => (
                                <li key={member.id}>
                                    {member.name} — {member.open_cards_count} card{member.open_cards_count === 1 ? '' : 's'} em aberto
                                </li>
                            ))}
                        </ul>
                        Os cards permanecem no painel; reatribua-os se necessário.
                    </div>
                }
            </div>
        </Modal>
    );
};

export default PipelineMembersModal;
